// ---------------------------------------------------------------------------
// What a page change does: title, <html lang>, scroll, focus (R12c, rule 6)
//
// A page change is every commit that changes the route's name or its params
// (decision c3: campaign A → B is one). A change of the fragment only is not.
//
//   Title  "<prefix><Title> · SDR Agent by NDI", written once per page change.
//          The prefix is the deployment's, as the shell writes it on load
//          (🔴<STAGE/> on staging); the router used to drop it (B10).
//   Lang   pageLang(path), from the committed state (B15: it was read from
//          location.pathname while the address was still the old one).
//   Scroll #main-content and the window to the top.
//   Focus  the new page's first h1, given tabindex="-1" when it has none,
//          then focus({ preventScroll }). Not on the first load (decision c2,
//          B9: the first Tab skipped the skip link and the nav).
//
// The focus step starts when remount.ts reports that it rendered the new key,
// and looks only inside what it rendered: the app root re-renders on a timer
// (functional-lit), so a step queued at the commit can find the OLD page's
// h1, which the render then removes (B16). If the h1 is not there yet — the
// campaign page shows a skeleton first (campaign-detail.ts:1761-1770) — it
// waits for one, up to `focusWait` (5 s), and focuses #main-content if none
// comes (B8).
//
// Focus moves unless, when the step runs, the person is already somewhere in
// the new page (an autofocus field, or a field they went to). The body,
// #main-content, a removed element and anything outside the new page — the
// nav, the skip link, the control that started the navigation — are not
// their place.
// ---------------------------------------------------------------------------

import { deployment, titlePrefix } from '../deployment.js';
import { pageLang } from '../page-lang.js';
import { onRemount, type RemountScope } from './remount.js';
import { type Commit, pageKey, type Router } from './router.js';
import type { RouteTable } from './routes.js';

export const APP_NAME = 'SDR Agent by NDI';

export interface PageChangeOptions {
  /** How long the focus step waits for a late h1 before it focuses #main-content. */
  focusWait?: number;
  /** The title prefix; the deployment's (deployment.ts) when left out. */
  prefix?: () => string;
}

/** The tab title for a route. */
export function pageTitle(routes: RouteTable, name: string, prefix = ''): string {
  const title = Object.hasOwn(routes, name) ? routes[name]?.title : undefined;
  return `${prefix}${title ? `${title} · ${APP_NAME}` : APP_NAME}`;
}

/** Applies a page change for every commit of `router`; returns the function that stops it. */
export function startPageChange<Name extends string>(
  router: Router<Name>,
  routes: RouteTable,
  options: PageChangeOptions = {},
): () => void {
  const focusWait = options.focusWait ?? 5000;
  const prefix = options.prefix ?? (() => titlePrefix(deployment()));
  /** The key whose page should get focus once it is rendered, or null. */
  let pending: string | null = null;
  /** The last key remount.ts reported, and what it rendered: a render may come before the commit's listener. */
  let rendered: { key: string; scope: RemountScope } | null = null;
  let stopWaiting: (() => void) | null = null;

  const cancel = () => {
    stopWaiting?.();
    stopWaiting = null;
  };

  const scrollToTop = () => {
    document.getElementById('main-content')?.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  /** True when focus is somewhere in the new page that the person (or the page) chose. */
  const theirPlace = (scope: RemountScope) => {
    const active = document.activeElement;
    return !!active && active !== document.body && active.isConnected && scope.contains(active);
  };

  const focus = (el: HTMLElement) => {
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    try {
      el.focus({ preventScroll: true });
    } catch {
      /* an element that cannot take focus keeps none */
    }
  };

  function focusStep(scope: RemountScope) {
    cancel();
    scrollToTop();
    const found = () => scope.query<HTMLElement>('h1');
    const land = (h1: HTMLElement | null) => {
      cancel();
      if (theirPlace(scope)) return;
      const target = h1 ?? document.getElementById('main-content');
      if (target) focus(target);
    };
    const now = found();
    if (now) return land(now);
    const observer = new MutationObserver(() => {
      const h1 = found();
      if (h1) land(h1);
    });
    const container = scope.container;
    if (container) observer.observe(container, { childList: true, subtree: true });
    const timer = setTimeout(() => land(null), focusWait);
    stopWaiting = () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }

  const stopRemount = onRemount((key, scope) => {
    rendered = { key, scope };
    if (key !== pending) return;
    pending = null;
    focusStep(scope);
  });

  const stopCommit = router.onCommit(({ state, previous, kind }: Commit<Name>) => {
    const key = pageKey(state);
    if (previous && pageKey(previous) === key) return;
    cancel();
    document.title = pageTitle(routes, state.name, prefix());
    document.documentElement.lang = pageLang(state.path);
    if (kind === 'start') return;
    if (rendered?.key === key) {
      // The page rendered before this listener ran (the root re-rendered inside the state write).
      pending = null;
      focusStep(rendered.scope);
    } else {
      pending = key;
    }
  });

  return () => {
    cancel();
    stopRemount();
    stopCommit();
    pending = null;
  };
}
